import { FC } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, ExternalLink, MessageSquare, UserCheck, Info } from 'lucide-react';

export type CtaFlowType = 'url_redirect' | 'guided_conversation' | 'expert_capture';

export interface CtaFlow {
  id: string;
  label: string;
  type: CtaFlowType;
  url?: string;
  starter_prompt?: string;
  confirmation_message?: string;
  hubspot_flag_field?: string | null;
}

interface ICtaFlowsEditorProps {
  flows: CtaFlow[];
  onChange: (flows: CtaFlow[]) => void;
}

const inputClass =
  'w-full h-9 px-3 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';
const textareaClass =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none';
const labelClass = 'block text-xs font-medium text-slate-700 mb-1';

const TYPE_META: Record<CtaFlowType, { icon: FC<{ className?: string }>; label: string; description: string }> = {
  url_redirect: {
    icon: ExternalLink,
    label: 'URL Redirect',
    description: 'Opens a specific page in a new tab — e.g. a pricing page, Calendly link, or resource.',
  },
  guided_conversation: {
    icon: MessageSquare,
    label: 'Guided Conversation',
    description: 'Sends a hidden starter prompt to the AI on the visitor\'s behalf, kicking off a specific conversation flow without the visitor having to type anything.',
  },
  expert_capture: {
    icon: UserCheck,
    label: 'Talk to an Expert',
    description: 'Shows a short in-chat form (name, use case, email). Submissions are saved as leads and can trigger a HubSpot workflow.',
  },
};

/**
 * Editor for the in-chat welcome buttons (cta_flows). These render inside the
 * conversation below the first greeting message, before the visitor sends anything.
 * They disappear once the visitor starts typing.
 */
const CtaFlowsEditor: FC<ICtaFlowsEditorProps> = ({ flows, onChange }) => {
  const patch = (index: number, partial: Partial<CtaFlow>) => {
    const updated = [...flows];
    updated[index] = { ...updated[index], ...partial };
    onChange(updated);
  };

  return (
    <div className='space-y-4'>
      {/* Surface indicator */}
      <div className='flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5'>
        <Info className='h-4 w-4 shrink-0 text-blue-500 mt-0.5' />
        <p className='text-xs text-blue-700 leading-relaxed'>
          These buttons appear <strong>inside the open chat</strong> just below your greeting, before the visitor types.
          They are different from the <strong>Pre-Chat Popup</strong> buttons (the floating card before the chat opens).
        </p>
      </div>

      {flows.length === 0 && (
        <div className='rounded-lg border border-dashed border-gray-200 py-6 px-4 text-center space-y-1'>
          <p className='text-sm font-medium text-gray-600'>No welcome buttons yet</p>
          <p className='text-xs text-gray-400'>
            Add a button to guide visitors toward demos, coverage checks, or expert conversations right from the greeting.
          </p>
        </div>
      )}

      {flows.map((flow, i) => {
        const meta = TYPE_META[flow.type] || TYPE_META.url_redirect;
        const TypeIcon = meta.icon;
        return (
          <div key={flow.id} className='border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50'>
            <div className='flex items-center justify-between'>
              <span className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>Button {i + 1}</span>
              <button
                type='button'
                onClick={() => onChange(flows.filter((_, idx) => idx !== i))}
                className='text-xs text-red-500 hover:text-red-700'
              >
                Remove
              </button>
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className={labelClass}>Button label *</label>
                <input
                  type='text'
                  value={flow.label}
                  placeholder='e.g. Check Coverage'
                  onChange={(e) => patch(i, { label: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Action type *</label>
                <select
                  value={flow.type}
                  onChange={(e) => patch(i, { type: e.target.value as CtaFlowType })}
                  className={inputClass}
                >
                  {(Object.keys(TYPE_META) as CtaFlowType[]).map((t) => (
                    <option key={t} value={t}>{TYPE_META[t].label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Type description */}
            <div className='flex items-start gap-1.5 rounded-md bg-white border border-slate-200 px-2.5 py-2'>
              <TypeIcon className='h-3.5 w-3.5 shrink-0 text-slate-400 mt-0.5' />
              <p className='text-xs text-slate-500 leading-relaxed'>{meta.description}</p>
            </div>

            {flow.type === 'url_redirect' && (
              <div>
                <label className={labelClass}>Destination URL *</label>
                <input
                  type='text'
                  value={flow.url || ''}
                  placeholder='https://example.com/page'
                  onChange={(e) => patch(i, { url: e.target.value })}
                  className={inputClass}
                />
              </div>
            )}

            {flow.type === 'guided_conversation' && (
              <div>
                <label className={labelClass}>Starter prompt *</label>
                <textarea
                  value={flow.starter_prompt || ''}
                  placeholder='e.g. I would like to explore use cases and find the right solution for my needs.'
                  rows={2}
                  onChange={(e) => patch(i, { starter_prompt: e.target.value })}
                  className={textareaClass}
                />
                <p className='text-xs text-slate-400 mt-1'>Sent silently to the AI to start the guided conversation when the visitor clicks.</p>
              </div>
            )}

            {flow.type === 'expert_capture' && (
              <>
                <div>
                  <label className={labelClass}>Confirmation message</label>
                  <textarea
                    value={flow.confirmation_message || ''}
                    placeholder='Thank you! A representative will contact you at the email address you provided as soon as possible.'
                    rows={2}
                    onChange={(e) => patch(i, { confirmation_message: e.target.value })}
                    className={textareaClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>HubSpot flag field <span className='text-slate-400 font-normal'>(optional)</span></label>
                  <input
                    type='text'
                    value={flow.hubspot_flag_field || ''}
                    placeholder='e.g. expert_request_flag'
                    onChange={(e) => patch(i, { hubspot_flag_field: e.target.value || null })}
                    className={inputClass}
                  />
                  <p className='text-xs text-slate-400 mt-1'>
                    When set, this HubSpot contact property is marked &quot;true&quot; on submission — use it to trigger a HubSpot workflow.
                  </p>
                </div>
              </>
            )}
          </div>
        );
      })}

      <button
        type='button'
        onClick={() => onChange([...flows, { id: uuidv4(), label: '', type: 'url_redirect', url: '' }])}
        className='flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:border-primary/40 hover:bg-primary/[0.04] hover:text-primary'
      >
        <Plus className='h-4 w-4' /> Add button
      </button>
    </div>
  );
};

export default CtaFlowsEditor;
